/**
 * "Travel to X?" — the confirmation a click on a map pin opens. It shows what the march involves (the server's
 * dice-free preview) and does nothing until the player presses Travel; Cancel, or a different pin, drops it.
 * Maz, 2026-10-21: definitely click to confirm, because travel spends days and can get the party lost.
 */

import { useEffect, useState } from 'react';

import { api } from '../api.js';
import { canTravel, describePreview, type JourneyPreviewView } from './travel.js';

export function TravelConfirm({
  campaignId,
  placeId,
  placeName,
  onTravel,
  onCancel,
}: {
  readonly campaignId: string;
  readonly placeId: string;
  /** The pin's own name, shown while the preview loads. */
  readonly placeName: string;
  readonly onTravel: (placeId: string) => void;
  readonly onCancel: () => void;
}) {
  const [preview, setPreview] = useState<JourneyPreviewView | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let current = true;
    setPreview(undefined);
    setFailed(false);
    void api
      .get<JourneyPreviewView>(`/api/campaigns/${campaignId}/journey-preview?to=${encodeURIComponent(placeId)}`)
      .then((body) => current && setPreview(body))
      .catch(() => current && setFailed(true));
    return () => {
      current = false;
    };
  }, [campaignId, placeId]);

  return (
    <div
      role="group"
      aria-label={`Travel to ${placeName}`}
      className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700"
    >
      <span className="font-semibold">Travel to {preview?.destinationName ?? placeName}?</span>
      <span className="text-xs text-neutral-600 dark:text-neutral-400">
        {failed ? 'The journey could not be looked up.' : preview ? describePreview(preview) : 'Working out the way…'}
      </span>
      <span className="ml-auto flex gap-2">
        <button
          type="button"
          disabled={!canTravel(preview)}
          onClick={() => onTravel(placeId)}
          className="rounded-md border border-neutral-300 px-3 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-40 dark:border-neutral-700"
        >
          Travel
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md px-3 py-1 text-sm underline underline-offset-2"
        >
          Cancel
        </button>
      </span>
    </div>
  );
}
